/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Separator,
  Alert,
  AlertDescription,
} from '@lb-classroom/ui';
import { i18n, withTranslation } from 'next-i18next';
import { TFunction } from 'i18next';
import classnames from 'classnames';
import { hasRole } from '@lb-classroom/feature-auth';
import * as AuthActions from '../../../actions/auth';
import * as ModalActions from '../../../actions/modal';
import * as FlashActions from '../../../actions/flash';
import TextInput from '../../UI/atoms/form/TextInput';
import appUtils, { isMobile } from '../../../utils/appUtils';
import { Turnstile } from '../../UI/atoms/Turnstile';
import { GoogleOAuthButton } from '../google-oauth-button';
import { FEATURE_FLAGS } from '../../../constants/feature-flags';

interface User {
  profile_type: string;
  onboarding_next_path?: string | null;
  locale: {
    i18n_form: string;
  };
}

interface GeoData {
  countryCode: string;
  ip: string;
  timeZone: string;
}

interface RootState {
  auth: {
    user: User | null;
    waiting: boolean;
  };
  geoData: {
    geoData: GeoData;
  };
}

interface StateProps {
  user: User | null;
  waiting: boolean;
  geoData: GeoData;
}

interface DispatchProps {
  authActions: typeof AuthActions;
  modalActions: typeof ModalActions;
  flashActions: typeof FlashActions;
}

interface OwnProps {
  t: TFunction;
  inModal?: boolean;
  clearModalMessage?: () => void;
}

type SignupProps = StateProps & DispatchProps & OwnProps;

function mapStateToProps(state: RootState): StateProps {
  const { auth, geoData } = state;
  const { user, waiting } = auth;
  return {
    user,
    waiting,
    geoData: geoData.geoData,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    authActions: bindActionCreators<any, any>(AuthActions, dispatch),
    modalActions: bindActionCreators<any, any>(ModalActions, dispatch),
    flashActions: bindActionCreators<any, any>(FlashActions, dispatch),
  };
}

const createSignupSchema = (t: TFunction) =>
  z.object({
    firstName: z
      .string()
      .min(1, {
        message: t('validators.isRequired', { name: t('form.firstName') }),
      }),
    lastName: z
      .string()
      .min(1, {
        message: t('validators.isRequired', { name: t('form.lastName') }),
      }),
    email: z
      .string()
      .min(1, {
        message: t('validators.isRequired', { name: t('form.email') }),
      })
      .regex(/^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i, {
        message: t('validators.emailFormatInvalid'),
      }),
    password: z
      .string()
      .min(1, {
        message: t('validators.isRequired', { name: t('form.password') }),
      })
      .min(6, { message: t('validators.PasswordtooShort') }),
  });

type SignupFormValues = z.infer<ReturnType<typeof createSignupSchema>>;

// Turnstile site key from environment variable
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

const Signup: React.FC<SignupProps> = (props) => {
  const router = useRouter();
  const language = i18n.language;
  const currentLocale = appUtils.getLocaleFromLang(language);
  const {
    t,
    inModal = false,
    authActions,
    modalActions,
    flashActions,
    geoData,
    user,
    waiting,
  } = props;
  const { ip, countryCode, timeZone } = geoData;

  // Turnstile token state
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);

  const signupSchema = createSignupSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (!props.inModal) {
      flashActions.clearMessage();
    }
  }, [props.inModal, flashActions]);

  // Track if redirect has happened to prevent duplicate redirects
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (hasRedirected.current) return;

    if (user) {
      hasRedirected.current = true;
      modalActions.hide();

      const isTutor = hasRole(user.profile_type, 'Tutor');
      const isStudent = hasRole(user.profile_type, 'Student');

      // Tutors (including admins with both roles) go to tutor dashboard
      if (isTutor) {
        router.push('/t/dashboard');
      } else if (isStudent) {
        // Students go to onboarding (if needed) with locale
        i18n.changeLanguage(user.locale.i18n_form);
        const nextPath = user.onboarding_next_path || '/s/dashboard';
        router.push(nextPath, nextPath, {
          locale: user.locale.i18n_form,
        });
      } else {
        // Unknown role, redirect to home
        router.push('/');
      }
    }
  }, [user, router, modalActions]);

  const hideModalPromise = () => {
    return new Promise<string>((resolve, reject) => {
      modalActions.hide();
      window.setTimeout(() => {
        resolve('hide');
      }, 300);
    });
  };

  const transitToLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (inModal) {
      await hideModalPromise();
      modalActions.show('login');
    } else {
      router.push('/login');
    }
  };

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token);
    setTurnstileError(false);
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setTurnstileError(true);
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  const onSubmit = (data: SignupFormValues) => {
    // Require Turnstile verification in production
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      if (inModal) {
        modalActions.addMessage(t('validators.captchaRequired') || 'Please complete the security check', { type: 'error', autoDisappear: false });
      } else {
        flashActions.addMessage(t('validators.captchaRequired') || 'Please complete the security check', { type: 'error', autoDisappear: false, sticky: false });
      }
      return;
    }

    modalActions.clearMessage();
    flashActions.clearMessage();

    const args = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      password: data.password,
      ip,
      country_code: countryCode,
      time_zone: timeZone,
      locale: currentLocale,
      turnstile_token: turnstileToken,
    };
    authActions.signup(args, inModal);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  const panelHeader = isMobile() ? (
    <div>
      <span>{t('modals.signup.signup')}</span>
    </div>
  ) : null;
  const panelBodyClass = classnames({ loading: waiting });

  return (
    <div className="max-w-md mx-auto px-0 sm:px-2">
      <Card>
        {panelHeader && (
          <CardHeader className="bg-slate-200">
            <CardTitle>{panelHeader}</CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className={panelBodyClass}>
            {FEATURE_FLAGS.googleOAuth && (
              <>
                <div className="mb-4">
                  <GoogleOAuthButton context="signup" />
                </div>
                <div className="flex items-center gap-3 my-4">
                  <Separator className="flex-1" />
                  <span className="text-sm text-slate-500">
                    {t('auth.orSeparator')}
                  </span>
                  <Separator className="flex-1" />
                </div>
              </>
            )}
            <form
              method="post"
              className="signup-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="control-group mb-4">
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      placeholder={t('form.firstName')}
                      className="first-name"
                      value={field.value}
                      name="first-name"
                      type="text"
                      updateHandler={(value) => field.onChange(value)}
                      validationResult={
                        errors.firstName
                          ? {
                              success: false,
                              message: errors.firstName.message,
                            }
                          : undefined
                      }
                      invalidLabel={true}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="control-group mb-4">
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      placeholder={t('form.lastName')}
                      className="last-name"
                      value={field.value}
                      name="last-name"
                      type="text"
                      updateHandler={(value) => field.onChange(value)}
                      validationResult={
                        errors.lastName
                          ? { success: false, message: errors.lastName.message }
                          : undefined
                      }
                      invalidLabel={true}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="control-group mb-4">
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      placeholder={t('form.email')}
                      className="email"
                      value={field.value}
                      name="email"
                      type="email"
                      updateHandler={(value) => field.onChange(value)}
                      validationResult={
                        errors.email
                          ? { success: false, message: errors.email.message }
                          : undefined
                      }
                      invalidLabel={true}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="control-group mb-4">
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      placeholder={t('form.password')}
                      className="password"
                      value={field.value}
                      name="password"
                      type="password"
                      updateHandler={(value) => field.onChange(value)}
                      validationResult={
                        errors.password
                          ? { success: false, message: errors.password.message }
                          : undefined
                      }
                      invalidLabel={true}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              {/* Cloudflare Turnstile CAPTCHA */}
              {TURNSTILE_SITE_KEY && (
                <div className="control-group mb-4">
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                    theme="auto"
                    language={language}
                    className="flex justify-center"
                  />
                  {turnstileError && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertDescription>
                        {t('validators.captchaError') || 'Security verification failed. Please try again.'}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
              <div className="control-group">
                <Button
                  className="w-full"
                  size="lg"
                  type="submit"
                  disabled={waiting || (TURNSTILE_SITE_KEY && !turnstileToken)}
                >
                  {t('modals.signup.signup')}
                </Button>
              </div>
            </form>
          </div>
          <Separator className="my-4" />
          <div className="flex flex-col items-center">
            <span>
              {t('modals.signup.hasAccount')}{' '}
              <a
                onClick={transitToLogin}
                className="text-link underline cursor-pointer hover:text-link-active"
              >
                {t('modals.signup.login')}
              </a>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(Signup);
export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
